import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import kanbanService from '@api/v1/kanbanService'
import useDocumentTitle from '@hooks/useDocumentTitle'
import useReadState from '@hooks/useReadState'
import LoadingSpinner from '@components/common/LoadingSpinner'
import { toast } from '@store/toastStore'

const KanbanBoard = () => {
  useDocumentTitle('Task Board')
  const queryClient = useQueryClient()
  const [selectedBoardId, setSelectedBoardId] = useState(null)

  const boardsQuery = useQuery({
    queryKey: ['kanban-boards'],
    queryFn: () => kanbanService.getBoards(),
  })

  const listsQuery = useQuery({
    queryKey: ['kanban-lists', selectedBoardId],
    queryFn: () => kanbanService.getLists(selectedBoardId),
    enabled: !!selectedBoardId,
  })

  const moveMutation = useMutation({
    mutationFn: (data) => kanbanService.moveTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kanban-lists', selectedBoardId] })
      toast.success('Task moved')
    },
    onError: (err) => toast.error(err.message || 'Failed to move task'),
  })

  /*
   * CC-53132 — three outcomes per read, not two. See `@api/v1/readState`.
   *
   * Both queries branched on `isLoading` and then on `length === 0`, and NEITHER
   * of them destructured `error` at all. `isLoading` is `isPending &&
   * isFetching`, so it is FALSE for a query that is pending but not in flight —
   * above all a PAUSED one (react-query's default `networkMode: 'online'` while
   * `onlineManager` believes the browser is offline). `queryFn` is never called,
   * nothing throws, nothing is logged, and `data` stays `undefined`, which
   * `Array.isArray(...) ? ... : []` turned into an empty array.
   *
   * So an unsent read and a thrown read BOTH rendered as **"No Kanban boards
   * found"** — with the "Create boards in the legacy Centerbase app" hint under
   * it, which actively sends the user off to do work they do not need to do —
   * and, one level down, as **"No task lists on this board"** about a board
   * whose lists were never fetched. The board picker also disappears in that
   * state, because it is gated on `boards.length > 0`, so the screen offers no
   * contradicting evidence at all.
   *
   * `useReadState` rather than the bare `readState` because it carries the
   * permanent blocked-read diagnostic: a paused fetch produces no request, no
   * error and no log, so without it the failing load leaves no trace anywhere.
   *
   * `idle` decision, both queries: FOLDED INTO LOADING.
   *   - `kanban-boards` has no `enabled` option, so it cannot reach idle.
   *   - `kanban-lists` IS conditionally enabled (`!!selectedBoardId`), so it is
   *     idle whenever no board is chosen — but that case never reaches these
   *     branches: the "Select a board to view tasks" prompt below is chosen
   *     first, and it is the honest rendering of "not asked yet". Inside the
   *     branch where a board IS selected the query is always enabled, so idle
   *     there is only the tick before the fetch starts. Folded in rather than
   *     left unhandled because an unhandled state renders NOTHING — a blank
   *     board is a worse bug than the one being fixed here.
   */
  const boardsState = useReadState('kanban-boards', boardsQuery)
  const listsState = useReadState(`kanban-lists:${selectedBoardId ?? 'none'}`, listsQuery)

  // Coerced only so the picker and the columns can be mapped. What decides
  // whether an empty result may be DESCRIBED as empty is the state above.
  const boards = Array.isArray(boardsQuery.data?.data) ? boardsQuery.data.data : []
  const lists = Array.isArray(listsQuery.data?.data) ? listsQuery.data.data : []

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display text-gray-900">Task Board</h1>
          <p className="text-sm text-gray-500 mt-1">Kanban-style task management</p>
        </div>
        {boardsState === 'ready' && boards.length > 0 && (
          <select
            aria-label="Select board"
            value={selectedBoardId || ''}
            onChange={(e) => setSelectedBoardId(e.target.value || null)}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-cb-accent/30"
          >
            <option value="">Select a board...</option>
            {boards.map((b, i) => (
              <option key={b.BoardId || b.Id || i} value={b.BoardId || b.Id}>
                {b.Name || b.BoardName || `Board ${i + 1}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {(boardsState === 'loading' || boardsState === 'idle') && (
        <div className="p-12 flex justify-center"><LoadingSpinner message="Loading boards..." /></div>
      )}

      {/* Paused: no request was sent. A spinner would be almost as wrong as the
          empty state, because a paused query never resolves on its own — it
          would spin for as long as the browser stayed offline. `role="alert"`
          so the correction is announced, not just drawn. */}
      {boardsState === 'blocked' && (
        <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
          <p className="font-medium">Can’t reach the server, so we haven’t loaded your boards.</p>
          <p className="mt-1 text-amber-800">
            This is not a firm with no boards, and there is nothing to create — the list was never
            requested. Your connection looks offline; check it and try again.
          </p>
          <button
            type="button"
            onClick={() => boardsQuery.refetch()}
            className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* A thrown read had no render site on this page at all — `error` was never
          read off the query — so every failure arrived dressed as an empty firm. */}
      {boardsState === 'failed' && (
        <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
          <p className="font-medium">Failed to load boards: {boardsQuery.error?.message}</p>
          <p className="mt-1">
            This is not a firm with no boards — nothing was read, so nothing here describes your data.
          </p>
          <button
            type="button"
            onClick={() => boardsQuery.refetch()}
            className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      {/* Only a completed read may say the firm has no boards. */}
      {boardsState === 'ready' && boards.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">No Kanban boards found</p>
          <p className="text-xs text-gray-400 mt-1">Create boards in the legacy Centerbase app to see them here</p>
        </div>
      )}

      {boardsState === 'ready' && boards.length > 0 && !selectedBoardId && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <p className="text-sm text-gray-500">Select a board to view tasks</p>
        </div>
      )}

      {boardsState === 'ready' && boards.length > 0 && selectedBoardId && (
        <>
          {(listsState === 'loading' || listsState === 'idle') && (
            <div className="p-12 flex justify-center"><LoadingSpinner message="Loading task lists..." /></div>
          )}

          {listsState === 'blocked' && (
            <div role="alert" className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
              <p className="font-medium">Can’t reach the server, so we haven’t loaded this board’s lists.</p>
              <p className="mt-1 text-amber-800">
                This is not an empty board — no request was sent. Your connection looks offline;
                check it and try again.
              </p>
              <button
                type="button"
                onClick={() => listsQuery.refetch()}
                className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
              >
                Try again
              </button>
            </div>
          )}

          {listsState === 'failed' && (
            <div role="alert" className="rounded-xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">
              <p className="font-medium">Failed to load task lists: {listsQuery.error?.message}</p>
              <p className="mt-1">This is not an empty board — its lists were never read.</p>
              <button
                type="button"
                onClick={() => listsQuery.refetch()}
                className="mt-3 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-100"
              >
                Try again
              </button>
            </div>
          )}

          {listsState === 'ready' && lists.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <p className="text-sm text-gray-500">No task lists on this board</p>
            </div>
          )}

          {/* The per-column "N tasks" counts live in here, and that is the point
              of the gate: a count rendered beside an unfinished read is a claim,
              not an observation. They are only ever drawn from a resolved
              payload now, by construction rather than by coincidence. */}
          {listsState === 'ready' && lists.length > 0 && (
            <div className="flex gap-4 overflow-x-auto pb-4" role="region" aria-label="Kanban columns">
              {lists.map((list, i) => {
                const listId = list.ListId || list.Id || i
                const listName = list.Name || list.ListName || `List ${i + 1}`
                const tasks = list.Tasks || list.tasks || list.Items || []
                return (
                  <div
                    key={listId}
                    className="bg-gray-50 rounded-xl border border-gray-200 min-w-[280px] max-w-[320px] flex-shrink-0"
                  >
                    <div className="px-4 py-3 border-b border-gray-200">
                      <h3 className="text-sm font-semibold text-gray-700">{listName}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{tasks.length} task{tasks.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="p-2 space-y-2 max-h-[calc(100vh-16rem)] overflow-y-auto">
                      {tasks.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-4">No tasks</p>
                      ) : (
                        tasks.map((task, j) => {
                          const taskId = task.TaskId || task.Id || task.ObjectId || j
                          const taskName = task.Name || task.Subject || task.Title || `Task ${j + 1}`
                          return (
                            <div
                              key={taskId}
                              className="bg-white rounded-lg border border-gray-100 px-3 py-2.5 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                            >
                              <p className="text-sm text-gray-900 font-medium">{taskName}</p>
                              {task.DueDate && (
                                <p className="text-xs text-gray-500 mt-1">Due: {task.DueDate}</p>
                              )}
                              {task.AssignedTo && (
                                <p className="text-xs text-gray-400 mt-0.5">{task.AssignedTo}</p>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default KanbanBoard
